import { defineMessages, type MessageDescriptor } from "react-intl";

import {
  getDefaultSocialPostPlatformForType,
  type StandaloneAssetGenerationPostType,
  type StandaloneAssetBatchCreateRequestV1,
} from "@runcheetah/shared";

const standaloneAssetFormatMessages = defineMessages({
  portraitLabel: {
    id: "standaloneAsset.format.portrait.label",
    defaultMessage: "Portrait post",
  },
  portraitDescription: {
    id: "standaloneAsset.format.portrait.description",
    defaultMessage: "Instagram feed image, 4:5.",
  },
  squareLabel: {
    id: "standaloneAsset.format.square.label",
    defaultMessage: "Square post",
  },
  squareDescription: {
    id: "standaloneAsset.format.square.description",
    defaultMessage: "Instagram feed image, 1:1.",
  },
  storyLabel: {
    id: "standaloneAsset.format.story.label",
    defaultMessage: "Instagram Story",
  },
  storyDescription: {
    id: "standaloneAsset.format.story.description",
    defaultMessage: "Static Instagram creative, 9:16.",
  },
  carouselPortraitLabel: {
    id: "standaloneAsset.format.carouselPortrait.label",
    defaultMessage: "Portrait carousel",
  },
  carouselPortraitDescription: {
    id: "standaloneAsset.format.carouselPortrait.description",
    defaultMessage: "Multi-slide Instagram feed carousel, 4:5.",
  },
  carouselSquareLabel: {
    id: "standaloneAsset.format.carouselSquare.label",
    defaultMessage: "Square carousel",
  },
  carouselSquareDescription: {
    id: "standaloneAsset.format.carouselSquare.description",
    defaultMessage: "Multi-slide Instagram feed carousel, 1:1.",
  },
  tiktokPortraitLabel: {
    id: "standaloneAsset.format.tiktokPortrait.label",
    defaultMessage: "TikTok portrait post",
  },
  tiktokPortraitDescription: {
    id: "standaloneAsset.format.tiktokPortrait.description",
    defaultMessage: "Static TikTok post, 9:16.",
  },
  tiktokCarouselPortraitLabel: {
    id: "standaloneAsset.format.tiktokCarouselPortrait.label",
    defaultMessage: "TikTok portrait carousel",
  },
  tiktokCarouselPortraitDescription: {
    id: "standaloneAsset.format.tiktokCarouselPortrait.description",
    defaultMessage: "Multi-slide static TikTok carousel, 9:16.",
  },
});

export const STANDALONE_ASSET_FORMATS: Array<{
  postType: StandaloneAssetGenerationPostType;
  label: MessageDescriptor;
  description: MessageDescriptor;
}> = [
  {
    postType: "feed_portrait",
    label: standaloneAssetFormatMessages.portraitLabel,
    description: standaloneAssetFormatMessages.portraitDescription,
  },
  {
    postType: "feed_square",
    label: standaloneAssetFormatMessages.squareLabel,
    description: standaloneAssetFormatMessages.squareDescription,
  },
  {
    postType: "story",
    label: standaloneAssetFormatMessages.storyLabel,
    description: standaloneAssetFormatMessages.storyDescription,
  },
  {
    postType: "carousel_portrait",
    label: standaloneAssetFormatMessages.carouselPortraitLabel,
    description: standaloneAssetFormatMessages.carouselPortraitDescription,
  },
  {
    postType: "carousel_square",
    label: standaloneAssetFormatMessages.carouselSquareLabel,
    description: standaloneAssetFormatMessages.carouselSquareDescription,
  },
  {
    postType: "tiktok_portrait",
    label: standaloneAssetFormatMessages.tiktokPortraitLabel,
    description: standaloneAssetFormatMessages.tiktokPortraitDescription,
  },
  {
    postType: "tiktok_carousel_portrait",
    label: standaloneAssetFormatMessages.tiktokCarouselPortraitLabel,
    description: standaloneAssetFormatMessages.tiktokCarouselPortraitDescription,
  },
];

export type StandaloneAssetPostType = (typeof STANDALONE_ASSET_FORMATS)[number]["postType"];
export type StandaloneAssetCounts = Record<StandaloneAssetPostType, number>;

export const createEmptyStandaloneAssetCounts = (): StandaloneAssetCounts => ({
  feed_portrait: 1,
  feed_square: 0,
  story: 0,
  carousel_portrait: 0,
  carousel_square: 0,
  tiktok_portrait: 0,
  tiktok_carousel_portrait: 0,
});

export const getStandaloneAssetCountTotal = (counts: StandaloneAssetCounts): number =>
  Object.values(counts).reduce((sum, value) => sum + value, 0);

export const toDatetimeLocalValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hour}:${minute}`;
};

export const getStandaloneAssetDefaultFirstScheduledFor = (now = new Date()): string => {
  const date = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  date.setMinutes(0, 0, 0);

  return toDatetimeLocalValue(date);
};

export const getStandaloneAssetFirstScheduledForOnDate = (date: Date, now = new Date()): string => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const selectedDate = new Date(date);
  selectedDate.setHours(0, 0, 0, 0);

  if (selectedDate.getTime() === today.getTime()) {
    const nextHour = new Date(now);
    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);

    return toDatetimeLocalValue(nextHour);
  }

  const defaultTime = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  defaultTime.setMinutes(0, 0, 0);
  selectedDate.setHours(defaultTime.getHours(), 0, 0, 0);

  return toDatetimeLocalValue(selectedDate);
};

export const updateStandaloneAssetCount = (input: {
  counts: StandaloneAssetCounts;
  postType: StandaloneAssetPostType;
  delta: number;
  maxTotal: number;
}): StandaloneAssetCounts => {
  const current = input.counts[input.postType];
  const nextValue = Math.max(0, current + input.delta);
  const nextCounts = { ...input.counts, [input.postType]: nextValue };
  const total = getStandaloneAssetCountTotal(nextCounts);

  if (total > input.maxTotal) {
    return input.counts;
  }

  return nextCounts;
};

export const buildStandaloneAssetCreateRequest = (input: {
  prompt: string;
  audienceOverride?: string | null;
  counts: StandaloneAssetCounts;
  firstScheduledFor: string;
  intervalHours: number;
}): StandaloneAssetBatchCreateRequestV1 => {
  const firstDate = new Date(input.firstScheduledFor);
  const intervalMs = input.intervalHours * 60 * 60 * 1000;
  const items = STANDALONE_ASSET_FORMATS.flatMap((format) =>
    Array.from({ length: input.counts[format.postType] }, (_, index) => ({
      scheduledFor: new Date(firstDate.getTime() + intervalMs * index).toISOString(),
      platform: getDefaultSocialPostPlatformForType(format.postType) ?? "instagram",
      postType: format.postType,
      brandPresence: "branded" as const,
    })),
  ).map((item, index) => ({
    ...item,
    scheduledFor: new Date(firstDate.getTime() + intervalMs * index).toISOString(),
  }));

  return {
    version: 1,
    prompt: input.prompt.trim(),
    audienceOverride: input.audienceOverride?.trim() || null,
    items,
  };
};
